<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import type { Locale } from '@/types';
import AboutHero from '@/components/AboutHero.vue';
import AboutInfomations from '@/components/AboutInfomations.vue';
import AboutHistory from '@/components/AboutHistory.vue';

const { locale } = useI18n<{ message: string }, Locale>();

// SEOメタタグ設定
useHead({
  title: computed(() =>
    locale.value === 'ja'
      ? '山下真和都(マナト) - About | yamashitamana.to'
      : 'Manato Yamashita - About | yamashitamana.to'
  ),
  meta: [
    {
      name: 'description',
      content: computed(() =>
        locale.value === 'ja'
          ? '山下真和都（山下マナト）のプロフィール。東京都市大学メディア情報学部でコンピュータサイエンスを学んでいます。Web開発、アニメーション、イラストレーション、動画編集などのクリエイティブ活動が趣味です！'
          : 'Profile of Manato Yamashita. Student at Tokyo City University studying Computer Science. Web development, animation, illustration and creative activities are my hobbies!'
      ),
    },
    {
      name: 'keywords',
      content: computed(() =>
        locale.value === 'ja'
          ? '山下真和都, 山下マナト, プロフィール, 東京都市大学, Web開発, アニメーション, イラスト'
          : 'Manato Yamashita, Profile, Tokyo City University, Web Development, Animation, Illustration'
      ),
    },
    {
      property: 'og:title',
      content: computed(() =>
        locale.value === 'ja'
          ? '山下真和都(マナト) - About | yamashitamana.to'
          : 'Manato Yamashita - About | yamashitamana.to'
      ),
    },
    {
      property: 'og:description',
      content: computed(() =>
        locale.value === 'ja'
          ? '山下真和都（山下マナト）のプロフィール。東京都市大学メディア情報学部でコンピュータサイエンスを学んでいます。Web開発、アニメーション、イラストレーション、動画編集などのクリエイティブ活動が趣味です！'
          : 'Profile of Manato Yamashita. Student at Tokyo City University studying Computer Science. Web development, animation, illustration and creative activities are my hobbies!'
      ),
    },
    {
      property: 'og:url',
      content: 'https://www.yamashitamana.to/about',
    },
    {
      property: 'og:type',
      content: 'profile',
    },
    {
      property: 'og:image',
      content: 'https://www.yamashitamana.to/山下真和都(マナト).webp',
    },
    {
      name: 'twitter:title',
      content: computed(() =>
        locale.value === 'ja' ? '山下真和都(マナト) - About' : 'Manato Yamashita - About'
      ),
    },
    {
      name: 'twitter:description',
      content: computed(() =>
        locale.value === 'ja'
          ? '山下真和都のプロフィール。東京都市大学の学生、クリエイター。'
          : 'Profile of Manato Yamashita. TCU student and creator.'
      ),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.yamashitamana.to/about',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: computed(() =>
        JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: locale.value === 'ja' ? '山下真和都' : 'Manato Yamashita',
          alternateName: ['山下マナト', 'ヤマシタマナト', 'Manato Yamashita'],
          url: 'https://www.yamashitamana.to/about',
          image: 'https://www.yamashitamana.to/山下真和都(マナト).webp',
          jobTitle: locale.value === 'ja' ? '学生 / エンジニア' : 'Student / Engineer',
          alumniOf: {
            '@type': 'CollegeOrUniversity',
            name: locale.value === 'ja' ? '東京都市大学' : 'Tokyo City University',
          },
          sameAs: [
            'https://bsky.app/profile/yamashita.bsky.social',
            'https://www.linkedin.com/in/yamashitamanato',
            'https://github.com/ManatoYamashita',
            'https://bento.me/ym',
            'https://twitter.com/tcu_dc',
            'https://www.instagram.com/yamashitamana.to',
            'https://www.youtube.com/manapuraza',
          ],
        })
      ),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://www.yamashitamana.to/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'About',
            item: 'https://www.yamashitamana.to/about',
          },
        ],
      }),
    },
  ],
});
</script>

<template>
  <main id="about" class="about">
    <AboutHero />
    <AboutInfomations />
    <AboutHistory />
  </main>
</template>

<style lang="css" scoped>
/* === ベーススタイル（モバイル: ~480px） === */
.about {
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  pointer-events: all;
}
</style>
